import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  cohortId: string; // 'all' or uuid
}

interface AnalysisRow {
  id: string;
  analysis_markdown: string;
  generated_at: string;
  projects_count: number;
  participants_count: number;
  model: string | null;
}

export function ProjectsAnalysisModal({
  open,
  onOpenChange,
  organizationId,
  cohortId,
}: Props) {
  const queryClient = useQueryClient();
  const disabled = !organizationId || organizationId === "all" || !open;

  const queryKey = useMemo(
    () => ["enterprise-projects-analysis", organizationId, cohortId],
    [organizationId, cohortId],
  );

  const { data, isLoading } = useQuery<AnalysisRow | null>({
    queryKey,
    enabled: !disabled,
    queryFn: async () => {
      let q = supabase
        .from("enterprise_project_analyses")
        .select(
          "id, analysis_markdown, generated_at, projects_count, participants_count, model",
        )
        .eq("organization_id", organizationId);
      q = cohortId && cohortId !== "all"
        ? q.eq("cohort_id", cohortId)
        : q.is("cohort_id", null);
      const { data, error } = await q.maybeSingle();
      if (error) throw error;
      return (data as AnalysisRow | null) ?? null;
    },
  });

  const generate = useMutation({
    mutationFn: async () => {
      const { data: res, error } = await supabase.functions.invoke(
        "analyze-enterprise-projects",
        {
          body: {
            organization_id: organizationId,
            cohort_id: cohortId,
          },
        },
      );
      if (error) throw error;
      if ((res as any)?.error) throw new Error((res as any).error);
      return res as AnalysisRow;
    },
    onSuccess: () => {
      toast.success("Análisis generado");
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (e: any) => {
      toast.error(e?.message || "Error generando análisis");
    },
  });

  const hasAnalysis = !!data?.analysis_markdown;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Análisis IA de proyectos
          </DialogTitle>
          <DialogDescription>
            Insights generados por IA sobre los proyectos de esta empresa
            {cohortId && cohortId !== "all" ? " y cohorte" : ""}: problemas
            abordados, herramientas, KPIs, cuellos de botella y oportunidades.
          </DialogDescription>
        </DialogHeader>

        {organizationId === "all" || !organizationId ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Selecciona una empresa específica para generar el análisis.
          </p>
        ) : isLoading ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-4/6" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">
                {hasAnalysis ? (
                  <>
                    Generado el{" "}
                    {new Date(data!.generated_at).toLocaleString("es-CO", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                    {" · "}
                    {data!.projects_count} proyectos ·{" "}
                    {data!.participants_count} participantes
                  </>
                ) : (
                  "Aún no se ha generado un análisis para esta combinación."
                )}
              </div>
              <Button
                size="sm"
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
                className="gap-2"
              >
                {generate.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : hasAnalysis ? (
                  <RefreshCw className="h-4 w-4" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {hasAnalysis ? "Regenerar" : "Generar análisis"}
              </Button>
            </div>

            {hasAnalysis ? (
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown>{data!.analysis_markdown}</ReactMarkdown>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground border rounded-md p-6 text-center">
                Pulsa "Generar análisis" para que la IA revise todos los
                proyectos de esta empresa y devuelva los insights clave.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
